import { Calculator, History, PenLine, Upload } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { ordersApi } from '@/features/orders/services/ordersApi';
import type { Order, OrderParty, PartyType } from '@/features/orders/types';
import { cn } from '@/lib/utils';
import { useCDData } from '../../hooks/useCDData';
import {
  useCreateTransaction,
  useRefreshCalculations,
  useSaveLineItems,
  useSaveLoanTerms,
  useSaveParties,
  useUpdateTransaction,
} from '../../hooks/useCDMutations';
import { useGeneratePDF } from '../../hooks/useClosingDisclosureQueries';
import { useSaveWithVersioning } from '../../hooks/useSaveWithVersioning';
import { closingDisclosureApi } from '../../services';
import {
  selectIsDirty,
  selectServerCalculations,
  selectServerLineItems,
  selectServerLoanTerms,
  selectServerParties,
  selectServerTransaction,
  useClosingDisclosureStore,
} from '../../store/closingDisclosureStore';
import type { CalculatedTotals, LineItem, LoanTerms, Party, Transaction } from '../../types';
// Import step components
import {
  ClosingCostsForm,
  LoanTermsForm,
  PartiesForm,
  ReviewForm,
  SellerCreditsForm,
  TransactionBasicsForm,
} from '../forms';
// Import versioning components
import { DocumentUploadSection, SaveOptionsDialog, VersionHistoryPanel } from '../versioning';

interface WizardStep {
  id: number;
  title: string;
  component: React.ComponentType<any>;
  isComplete: boolean;
}

interface CreateCDWizardProps {
  transactionId?: string;
  orderId?: string;
}

const CreateCDWizard: React.FC<CreateCDWizardProps> = ({
  transactionId: propTransactionId,
  orderId,
}) => {
  const { id: paramTransactionId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const transactionId = propTransactionId || paramTransactionId;
  const isEditMode = !!transactionId;
  const isFromOrder = !!orderId;

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [sourceOrder, setSourceOrder] = useState<Order | null>(null);

  // ============================================================================
  // Zustand Store - Source of truth for CD data
  // ============================================================================
  const storeTransaction = useClosingDisclosureStore(selectServerTransaction);
  const storeLoanTerms = useClosingDisclosureStore(selectServerLoanTerms);
  const storeParties = useClosingDisclosureStore(selectServerParties);
  const storeLineItems = useClosingDisclosureStore(selectServerLineItems);
  const storeCalculations = useClosingDisclosureStore(selectServerCalculations);
  const isDirty = useClosingDisclosureStore(selectIsDirty);

  // Local state for create mode or order-based creation (before transaction exists)
  // Once a transaction is created, mutations update the Zustand store directly
  const [localTransaction, setLocalTransaction] = useState<Partial<Transaction> | null>(null);
  const [localLoanTerms] = useState<Partial<LoanTerms> | null>(null);
  const [localParties, setLocalParties] = useState<Party[]>([]);
  const [localLineItems] = useState<LineItem[]>([]);
  const [localCalculations] = useState<CalculatedTotals | null>(null);

  // Use store data when available (edit mode), otherwise use local state (create mode)
  const transaction = storeTransaction || localTransaction;
  const loanTerms = storeLoanTerms || localLoanTerms;
  const parties = storeParties.length > 0 ? storeParties : localParties;
  const lineItems = storeLineItems.length > 0 ? storeLineItems : localLineItems;
  const calculations = storeCalculations || localCalculations;

  // ============================================================================
  // TanStack Query - Data fetching and store hydration
  // ============================================================================
  const {
    isLoading: isDataLoading,
    isError: isDataError,
    error: dataError,
  } = useCDData({
    transactionId,
    mode: isEditMode ? 'edit' : 'create',
    enabled: isEditMode,
    autoInitialize: true,
  });

  // ============================================================================
  // Mutations - Update API and Zustand store together
  // ============================================================================
  const createTransactionMutation = useCreateTransaction();
  const updateTransactionMutation = useUpdateTransaction();
  const saveLoanTermsMutation = useSaveLoanTerms();
  const savePartiesMutation = useSaveParties();
  const saveLineItemsMutation = useSaveLineItems();
  const refreshCalculations = useRefreshCalculations();
  const generatePDFMutation = useGeneratePDF();

  // ============================================================================
  // Versioning
  // ============================================================================
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] = useState(false);

  const handleSaveDraftOnly = useCallback(async () => {
    if (!transaction?.id) return;
    // Use mutation to save draft - automatically updates store and invalidates cache
    await updateTransactionMutation.mutateAsync({
      id: transaction.id,
      transaction: {},
    });
    navigate('/dashboard/drafts');
  }, [transaction?.id, navigate, updateTransactionMutation]);

  const versioning = useSaveWithVersioning({
    transactionId: transaction?.id || '',
    transactionStatus: (transaction?.status as any) || 'draft',
    onSaveDraft: handleSaveDraftOnly,
    onVersionCreated: (_versionNumber: string | number) => {
      navigate('/dashboard/drafts');
    },
  });

  // Page header context
  const { setPageHeader, clearPageHeader } = usePageHeader();

  // ============================================================================
  // Load order data for creating CD from an order
  // ============================================================================
  useEffect(() => {
    const loadOrderData = async () => {
      if (!orderId || isEditMode) return;

      setIsLoading(true);
      setError(null);

      try {
        const orderData = await ordersApi.getById(orderId);
        setSourceOrder(orderData);

        // Build multiline property address from order fields
        const streetLine = orderData.property_unit
          ? `${orderData.property_address}, ${orderData.property_unit}`
          : orderData.property_address;
        const cityLine = `${orderData.property_city}, ${orderData.property_state} ${orderData.property_zip}`;
        const multilineAddress = `${streetLine}\n${cityLine}`;

        // Pre-populate transaction data from order
        setLocalTransaction({
          order_id: orderId,
          property_address: multilineAddress,
          sale_price: orderData.sale_price ? Number.parseFloat(orderData.sale_price) : undefined,
          closing_date: orderData.target_closing_date || undefined,
          file_number: orderData.order_number,
        });

        // Map order parties to CD parties
        if (orderData.parties && orderData.parties.length > 0) {
          const partyTypeMap: Record<PartyType, Party['party_type'] | null> = {
            buyer: 'borrower',
            seller: 'seller',
            lender: 'lender',
            listing_agent: 'real_estate_broker_seller',
            selling_agent: 'real_estate_broker_buyer',
            other: null,
          };

          const mappedParties: Party[] = orderData.parties
            .filter((p: OrderParty) => partyTypeMap[p.party_type] !== null)
            .map((orderParty: OrderParty) => ({
              id: '',
              draft_id: '',
              party_type: partyTypeMap[orderParty.party_type]!,
              name: orderParty.name,
              email: orderParty.email || undefined,
              phone: orderParty.phone || undefined,
              address_street: orderParty.address_street || undefined,
              address_city: orderParty.address_city || undefined,
              address_state: orderParty.address_state || undefined,
              address_zip_code: orderParty.address_zip || undefined,
              is_primary: orderParty.is_primary,
            }));

          setLocalParties(mappedParties);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load order data');
      } finally {
        setIsLoading(false);
      }
    };

    loadOrderData();
  }, [orderId, isEditMode]);

  // ============================================================================
  // Step Configuration
  // ============================================================================
  const steps: WizardStep[] = [
    {
      id: 1,
      title: 'Transaction Basics',
      component: TransactionBasicsForm,
      isComplete: !!transaction?.id,
    },
    {
      id: 2,
      title: 'Loan Information',
      component: LoanTermsForm,
      isComplete: !!loanTerms?.id,
    },
    {
      id: 3,
      title: 'Parties',
      component: PartiesForm,
      isComplete: parties.length > 0,
    },
    {
      id: 4,
      title: 'Closing Costs',
      component: ClosingCostsForm,
      isComplete: lineItems.length > 0,
    },
    {
      id: 5,
      title: 'Seller Credits',
      component: SellerCreditsForm,
      isComplete: true,
    },
    {
      id: 6,
      title: 'Review & Generate',
      component: ReviewForm,
      isComplete: false,
    },
  ];

  // ============================================================================
  // Data Fetching Helpers
  // ============================================================================
  // Use refs to avoid infinite loops - mutation objects change on every render
  const refreshCalculationsRef = useRef(refreshCalculations);
  refreshCalculationsRef.current = refreshCalculations;

  const saveLineItemsMutationRef = useRef(saveLineItemsMutation);
  saveLineItemsMutationRef.current = saveLineItemsMutation;

  const fetchCalculations = useCallback(async () => {
    if (!transaction?.id) return;

    setIsCalculating(true);
    try {
      // Use the refresh function to fetch and update both API cache and Zustand store
      // Use more retries (3) and longer delay (750ms) to handle backend processing time
      await refreshCalculationsRef.current(transaction.id, 3, 750);
      // Sync formData from updated serverData so UI reflects new values
      useClosingDisclosureStore.getState().syncFormDataFromServer();
    } catch (err) {
      console.error('Failed to fetch calculations:', err);
    } finally {
      setIsCalculating(false);
    }
  }, [transaction?.id]);

  // Refresh calculations when entering Review step
  useEffect(() => {
    if (currentStep === 6 && transaction?.id) {
      fetchCalculations();
    }
  }, [currentStep, transaction?.id, fetchCalculations]);

  // ============================================================================
  // Form Handlers
  // ============================================================================

  // Auto-save handler for line items
  const handleSaveLineItems = useCallback(
    async (data: { lineItems: LineItem[] }) => {
      if (!transaction?.id) return;

      try {
        // Use mutation ref to save line items - updates both API and Zustand store
        await saveLineItemsMutationRef.current.mutateAsync({
          transactionId: transaction.id,
          items: data.lineItems,
        });
        // Refresh calculations after saving line items
        await fetchCalculations();
      } catch (err: unknown) {
        console.error('Failed to auto-save line items:', err);
        throw err;
      }
    },
    [transaction?.id, fetchCalculations],
  );

  const validateCurrentStep = async () => {
    if (!transaction?.id) return false;

    try {
      const result = await closingDisclosureApi.validateTransaction(transaction.id);
      if (result.success) {
        setValidationErrors(result.data.errors);
        return result.data.isValid;
      }
      return false;
    } catch (err) {
      console.error('Validation failed:', err);
      return false;
    }
  };

  const handleNext = async (data?: any) => {
    setError(null);

    try {
      if (data) {
        switch (currentStep) {
          case 1: // Transaction Basics
            if (!transaction?.id) {
              // Create new transaction - mutation handles store hydration
              const newTransaction = await createTransactionMutation.mutateAsync(data);
              setLocalTransaction(newTransaction as Partial<Transaction>);
            } else {
              // Update existing transaction - mutation updates store
              await updateTransactionMutation.mutateAsync({
                id: transaction.id,
                transaction: data,
              });
            }
            break;

          case 2: // Loan Terms
            if (transaction?.id) {
              // Save loan terms - mutation updates store
              await saveLoanTermsMutation.mutateAsync({
                transactionId: transaction.id,
                loanTerms: data as Omit<LoanTerms, 'id' | 'transaction_id'>,
              });
            }
            break;

          case 3: // Parties
            if (transaction?.id && data.parties) {
              // Save parties - mutation updates store
              await savePartiesMutation.mutateAsync({
                transactionId: transaction.id,
                parties: data.parties,
              });
            }
            break;

          case 4: // Closing Costs
            if (transaction?.id && data?.lineItems) {
              // Save line items - mutation updates store and invalidates calculations
              await saveLineItemsMutation.mutateAsync({
                transactionId: transaction.id,
                items: data.lineItems,
              });
              await fetchCalculations();
            }
            break;

          case 5: // Seller Credits
            if (transaction?.id && data) {
              const sellerCreditItems: Partial<LineItem>[] = [];

              if (data.depositAmount) {
                sellerCreditItems.push({
                  section: 'L',
                  lineNumberString: 'L01',
                  description: `Deposit/Earnest Money${data.depositHeldBy ? ` (held by ${data.depositHeldBy})` : ''}`,
                  borrowerPaidAtClosing: data.depositAmount,
                  sellerPaidAtClosing: 0,
                });
              }

              if (data.sellerConcessions) {
                sellerCreditItems.push({
                  section: 'L',
                  lineNumberString: 'L02',
                  description: data.sellerConcessionsDescription || 'Seller Concessions',
                  borrowerPaidAtClosing: 0,
                  sellerPaidAtClosing: data.sellerConcessions,
                });
              }

              if (data.propertyTaxProration) {
                sellerCreditItems.push({
                  section: 'L',
                  lineNumberString: 'L03',
                  description: `Property Tax Proration${data.propertyTaxProrationPeriod ? ` (${data.propertyTaxProrationPeriod})` : ''}`,
                  borrowerPaidAtClosing: 0,
                  sellerPaidAtClosing: data.propertyTaxProration,
                });
              }

              if (data.hoaProration) {
                sellerCreditItems.push({
                  section: 'L',
                  lineNumberString: 'L04',
                  description: `HOA Proration${data.hoaProrationPeriod ? ` (${data.hoaProrationPeriod})` : ''}`,
                  borrowerPaidAtClosing: 0,
                  sellerPaidAtClosing: data.hoaProration,
                });
              }

              if (data.adjustments?.length) {
                data.adjustments.forEach((adj: any, index: number) => {
                  if (adj.amount) {
                    sellerCreditItems.push({
                      section: 'L',
                      lineNumberString: `L${String(5 + index).padStart(2, '0')}`,
                      description: adj.description || 'Adjustment',
                      borrowerPaidAtClosing: adj.creditTo === 'borrower' ? adj.amount : 0,
                      sellerPaidAtClosing: adj.creditTo === 'seller' ? adj.amount : 0,
                    });
                  }
                });
              }

              if (sellerCreditItems.length > 0) {
                // Save seller credit line items - mutation updates store
                await saveLineItemsMutation.mutateAsync({
                  transactionId: transaction.id,
                  items: [
                    ...lineItems.filter((li) => li.section !== 'L'),
                    ...(sellerCreditItems as LineItem[]),
                  ],
                });
                await fetchCalculations();
              }
            }
            break;

          case 6: // Review & Generate
            if (transaction?.id) {
              setIsLoading(true);
              try {
                // Update transaction - mutation updates store
                await updateTransactionMutation.mutateAsync({
                  id: transaction.id,
                  transaction: data,
                });

                if (versioning.shouldPrompt) {
                  versioning.openSaveDialog();
                  setIsLoading(false);
                } else {
                  navigate('/dashboard/drafts');
                }
              } catch (err) {
                setIsLoading(false);
                throw err;
              }
            }
            break;
        }
      }

      if (currentStep < 6) {
        setCurrentStep(currentStep + 1);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGeneratePDF = async (version: 'draft' | 'final') => {
    if (!transaction?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const isValid = await validateCurrentStep();
      if (!isValid && version === 'final') {
        setError('Please fix validation errors before generating final PDF');
        setIsLoading(false);
        return;
      }

      // Use mutation to generate PDF
      await generatePDFMutation.mutateAsync({
        transactionId: transaction.id,
        version,
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate PDF';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const currentStepData = steps[currentStep - 1];
  const CurrentStepComponent = currentStepData?.component;

  const canNavigateToStep = (stepId: number): boolean => {
    if (isEditMode) return true;
    return stepId <= currentStep || (steps[stepId - 1]?.isComplete ?? false);
  };

  const handleStepClick = (stepId: number) => {
    if (canNavigateToStep(stepId)) {
      setCurrentStep(stepId);
    }
  };

  // Page title based on mode
  const pageTitle = isEditMode
    ? 'Edit Closing Disclosure'
    : isFromOrder
      ? 'Create CD from Order'
      : 'Create Closing Disclosure';

  // Set page header
  useEffect(() => {
    const breadcrumbs = [
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Closing Disclosures', href: '/dashboard/drafts' },
      { label: pageTitle },
    ];

    const headerActions =
      isEditMode && transactionId ? (
        <>
          <Button variant="outline" size="sm" onClick={() => setIsDocumentUploadOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Upload Source</span>
            <span className="sm:hidden">Upload</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsVersionHistoryOpen(true)}>
            <History className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Version History</span>
            <span className="sm:hidden">History</span>
          </Button>
        </>
      ) : undefined;

    setPageHeader({
      title: pageTitle,
      breadcrumbs,
      actions: headerActions,
    });

    return () => clearPageHeader();
  }, [pageTitle, isEditMode, transactionId, setPageHeader, clearPageHeader]);

  // Show loading state while data is being fetched
  if (isEditMode && isDataLoading) {
    return (
      <div className="max-w-7xl">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  // Show error if data fetch failed
  if (isEditMode && isDataError) {
    return (
      <div className="max-w-7xl">
        <Alert className="border-destructive/30 bg-destructive/10">
          <AlertDescription className="text-destructive">
            {dataError?.message || 'Failed to load transaction data'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show loading for order-based creation
  if (isFromOrder && isLoading && !sourceOrder) {
    return (
      <div className="max-w-7xl">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      {/* Order context info */}
      {isFromOrder && sourceOrder && (
        <p className="text-muted-foreground text-sm mb-4">
          Order {sourceOrder.order_number} - {sourceOrder.property_address}
        </p>
      )}

      {/* Step Indicator */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex items-center min-w-max">
          {steps.map((step, index) => {
            const isClickable = canNavigateToStep(step.id);
            const isCompleted = currentStep > step.id || step.isComplete;
            const isCurrent = currentStep === step.id;

            return (
              <div
                key={step.id}
                className={`flex items-center ${index < steps.length - 1 ? 'flex-1 min-w-0' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => handleStepClick(step.id)}
                  disabled={!isClickable}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-2 py-1 transition-colors',
                    isClickable && !isCurrent && 'hover:bg-muted cursor-pointer',
                    !isClickable && 'cursor-not-allowed opacity-60',
                  )}
                >
                  <div
                    className={cn(
                      'w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0 transition-colors',
                      isCompleted
                        ? 'bg-green-600 dark:bg-green-500 text-white'
                        : isCurrent
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {isCompleted && !isCurrent ? '✓' : step.id}
                  </div>
                  <span
                    className={cn(
                      'text-xs sm:text-sm whitespace-nowrap',
                      isCurrent ? 'text-primary font-medium' : 'text-muted-foreground',
                    )}
                  >
                    {step.title}
                  </span>
                </button>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-px bg-border mx-2 sm:mx-4 min-w-4" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Status Indicators */}
      {((isEditMode && isDirty) || isCalculating) && (
        <div className="flex gap-2 justify-center mb-4">
          {isEditMode && isDirty && (
            <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">
              <PenLine className="h-3 w-3 mr-1" />
              Unsaved Changes
            </Badge>
          )}
          {isCalculating && (
            <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50">
              <Calculator className="h-3 w-3 mr-1 animate-pulse" />
              Calculating...
            </Badge>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <Alert className="mb-6 border-destructive/30 bg-destructive/10">
          <AlertDescription className="text-destructive">{error}</AlertDescription>
        </Alert>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert className="mb-6 border-yellow-500/30 bg-yellow-500/10">
          <AlertDescription>
            <div className="text-yellow-700 dark:text-yellow-400">
              <p className="font-medium mb-2">Please review the following:</p>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((err, idx) => (
                  <li key={idx} className="text-sm">
                    {err.message}
                  </li>
                ))}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Current Step Form */}
      <Card>
        <CardContent className="pt-6">
          {CurrentStepComponent && (
            <CurrentStepComponent
              key={`step-${currentStep}`}
              transaction={transaction}
              loanTerms={loanTerms}
              parties={parties}
              lineItems={lineItems}
              calculations={calculations}
              transactionId={transaction?.id}
              onNext={handleNext}
              onBack={handleBack}
              onGoToStep={setCurrentStep}
              onGeneratePDF={handleGeneratePDF}
              onSave={currentStep === 4 ? handleSaveLineItems : undefined}
              isLoading={isLoading}
              onRecalculate={currentStep === 6 ? fetchCalculations : undefined}
              isCalculating={isCalculating}
            />
          )}
        </CardContent>
      </Card>

      {/* Versioning Dialogs */}
      {isEditMode && transactionId && (
        <>
          <VersionHistoryPanel
            open={isVersionHistoryOpen}
            onOpenChange={setIsVersionHistoryOpen}
            transactionId={transactionId}
            onVersionRestored={() => {
              window.location.reload();
            }}
          />

          {isDocumentUploadOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
              <div className="bg-background rounded-lg p-6 w-full max-w-lg mx-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Upload Source Document</h2>
                  <Button variant="ghost" size="sm" onClick={() => setIsDocumentUploadOpen(false)}>
                    Close
                  </Button>
                </div>
                <DocumentUploadSection
                  transactionId={transactionId}
                  createVersionOnUpload={true}
                  onVersionCreated={() => {
                    setIsDocumentUploadOpen(false);
                  }}
                />
              </div>
            </div>
          )}

          <SaveOptionsDialog
            open={versioning.showSaveDialog}
            onOpenChange={(open) => {
              if (!open) versioning.closeSaveDialog();
            }}
            transactionStatus={(transaction?.status as any) || 'draft'}
            isSaving={versioning.isSaving}
            error={versioning.error}
            onSave={versioning.executeSave}
          />
        </>
      )}
    </div>
  );
};

export default CreateCDWizard;
